import { Alert, Typography } from '@mui/material';
import React from 'react';
import type { RegistryDocument, RegistryState } from '../data/types';
import { computeHoldings } from '../domain/holdings';
import { investorSummary } from '../domain/investorSummary';
import { date, money, unitPrice, units as formatUnits } from '../lib/format';
import { LEDGER_LABELS } from '../lib/labels';
import DocumentPaper, { DocTable } from './DocumentPaper';

interface ViewProps {
  state: RegistryState;
  doc: RegistryDocument;
}

function context(state: RegistryState, doc: RegistryDocument) {
  const holder = state.holders.find((item) => item.id === doc.holderId);
  const tenant = state.tenants.find((item) => item.id === holder?.tenantId) ?? state.tenants[0];
  const fund = state.funds.find((item) => item.id === doc.fundId);
  return { holder, tenant, fund };
}

function InvestmentToDate({
  state,
  holderId,
  fundId,
  asAt,
}: {
  state: RegistryState;
  holderId: string;
  fundId: string;
  asAt: string;
}) {
  const summary = investorSummary(state, holderId, asAt, asAt, [fundId]);
  if (!summary.length) return null;
  const sum = (pick: (item: (typeof summary)[number]) => number) =>
    summary.reduce((total, item) => total + pick(item), 0);
  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Your investment to date
      </Typography>
      <DocTable
        head={['Invested', 'Income received', 'Capital returned', 'Value now']}
        rows={[
          [
            money(sum((item) => item.invested)),
            money(sum((item) => item.incomeReceived)),
            money(sum((item) => item.capitalReturned)),
            money(sum((item) => item.value)),
          ],
        ]}
      />
    </>
  );
}

export function DistributionAdviceView({ state, doc }: ViewProps) {
  const { holder, tenant, fund } = context(state, doc);
  const distribution = state.distributions.find((item) => item.id === doc.refId);
  const line = distribution?.lines.find((item) => item.holderId === doc.holderId);
  if (!distribution || !line || !holder)
    return <Alert severity="info">Advice details are not available.</Alert>;
  const withheld = line.tfnWithholding + line.nrWithholding;
  return (
    <DocumentPaper
      tenant={tenant}
      holder={holder}
      issued={distribution.paymentDate}
      title="Distribution advice"
      subtitle={`${fund?.name} · period ${date(distribution.periodStart)} to ${date(distribution.periodEnd)}`}
    >
      <DocTable
        head={['', 'Amount']}
        rows={[
          ['Units held at record date', formatUnits(line.units)],
          ['Rate', `${distribution.centsPerUnit} cents per unit`],
          ['Gross distribution', money(line.gross, true)],
          ['TFN withholding', money(line.tfnWithholding, true)],
          ['Non-resident withholding', money(line.nrWithholding, true)],
        ]}
        total={[
          line.method === 'DRP' ? 'Net amount reinvested' : 'Net amount paid',
          money(line.net, true),
        ]}
      />
      <Typography variant="body2" sx={{ mt: 2 }}>
        {line.method === 'DRP'
          ? `Reinvested under the distribution reinvestment plan on ${date(distribution.paymentDate)}.`
          : `Paid on ${date(distribution.paymentDate)} to ${holder.bank.accountName}, BSB ${holder.bank.bsb}, account ending ${holder.bank.accountNumber.slice(-3)}.`}
      </Typography>
      <InvestmentToDate
        state={state}
        holderId={holder.id}
        fundId={distribution.fundId}
        asAt={distribution.paymentDate}
      />
      {withheld > 0 && holder.tfnStatus === 'NOT_QUOTED' && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          We withheld tax because we don&apos;t hold your TFN or ABN. You can add it in the app at
          any time.
        </Typography>
      )}
    </DocumentPaper>
  );
}

export function CapitalReturnAdviceView({ state, doc }: ViewProps) {
  const { holder, tenant, fund } = context(state, doc);
  const capitalReturn = state.capitalReturns.find((item) => item.id === doc.refId);
  const line = capitalReturn?.lines.find((item) => item.holderId === doc.holderId);
  if (!capitalReturn || !line || !holder)
    return <Alert severity="info">Advice details are not available.</Alert>;
  return (
    <DocumentPaper
      tenant={tenant}
      holder={holder}
      issued={capitalReturn.paymentDate}
      title="Capital return advice"
      subtitle={fund?.name}
    >
      <Typography variant="body2" sx={{ mb: 2 }}>
        {capitalReturn.reason}
      </Typography>
      <DocTable
        head={['', 'Amount']}
        rows={[
          ['Units held at record date', formatUnits(line.unitsHeld)],
          ['Units cancelled', formatUnits(line.unitsCancelled)],
          ['Capital returned (at $1.00 per unit)', money(line.amount, true)],
          ['Final income to repayment date', money(line.finalIncome, true)],
        ]}
        total={['Total paid', money(line.amount + line.finalIncome, true)]}
      />
      <Typography variant="body2" sx={{ mt: 2 }}>
        Paid on {date(capitalReturn.paymentDate)} to your nominated account.{' '}
        {capitalReturn.kind === 'FINAL'
          ? 'The trust has now been wound up and your holding is closed.'
          : 'Your remaining units continue to earn income.'}
      </Typography>
      <InvestmentToDate
        state={state}
        holderId={holder.id}
        fundId={capitalReturn.fundId}
        asAt={capitalReturn.paymentDate}
      />
    </DocumentPaper>
  );
}

export function ExitStatementView({ state, doc }: ViewProps) {
  const { holder, tenant, fund } = context(state, doc);
  if (!holder || !fund) return <Alert severity="info">Statement details are not available.</Alert>;
  const entries = state.ledger
    .filter((entry) => entry.holderId === holder.id && entry.fundId === fund.id)
    .sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate));
  const total = (types: string[]) =>
    entries
      .filter((entry) => types.includes(entry.type))
      .reduce((sum, entry) => sum + entry.amount, 0);
  const invested = total([
    'ISSUE',
    'CALL_PAYMENT',
    'MIGRATION_OPENING',
    'TRANSFER_IN',
    'DRP_ISSUE',
  ]);
  const returned = total(['CAPITAL_RETURN', 'REDEMPTION']);
  const income = total(['DISTRIBUTION']);
  return (
    <DocumentPaper
      tenant={tenant}
      holder={holder}
      issued={doc.date}
      title="Exit statement"
      subtitle={`${fund.name} · investment closed`}
    >
      <DocTable
        head={['Summary', 'Amount']}
        rows={[
          ['Capital invested', money(invested, true)],
          ['Capital returned', money(returned, true)],
          ['Income received (net)', money(income, true)],
        ]}
        total={['Total return on capital', money(returned + income - invested, true)]}
      />
      <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
        Transactions
      </Typography>
      <DocTable
        head={['Date and type', 'Units', 'Amount']}
        rows={entries.map((entry) => [
          `${date(entry.effectiveDate)} · ${LEDGER_LABELS[entry.type]}`,
          entry.units ? formatUnits(entry.units) : '—',
          money(entry.amount, true),
        ])}
      />
    </DocumentPaper>
  );
}

export function HoldingStatementView({ state, doc }: ViewProps) {
  const { holder, tenant } = context(state, doc);
  if (!holder) return <Alert severity="info">Statement details are not available.</Alert>;
  const holdings = computeHoldings(state.ledger, state.classes, state.commitments, doc.date).filter(
    (holding) => holding.holderId === holder.id,
  );
  return (
    <DocumentPaper
      tenant={tenant}
      holder={holder}
      issued={doc.date}
      title="Holding statement"
      subtitle={`As at ${date(doc.date)}`}
    >
      <DocTable
        head={['Trust and class', 'Units', 'Unit price', 'Value']}
        rows={holdings.map((holding) => {
          const unitClass = state.classes.find((item) => item.id === holding.classId);
          return [
            `${state.funds.find((fund) => fund.id === holding.fundId)?.name} · ${unitClass?.name}`,
            formatUnits(holding.units),
            unitPrice(holding.units ? holding.value / holding.units : 1),
            money(holding.value, true),
          ];
        })}
        total={[
          'Total',
          '',
          '',
          money(
            holdings.reduce((sum, holding) => sum + holding.value, 0),
            true,
          ),
        ]}
      />
    </DocumentPaper>
  );
}

export function LedgerLetterView({ state, doc }: ViewProps) {
  const { holder, tenant, fund } = context(state, doc);
  if (!holder) return <Alert severity="info">Document details are not available.</Alert>;
  const entries = state.ledger.filter(
    (entry) =>
      entry.holderId === holder.id &&
      (entry.eventId === doc.refId || entry.reference === doc.refId),
  );
  const titles: Partial<Record<RegistryDocument['kind'], string>> = {
    REDEMPTION_CONFIRMATION: 'Redemption confirmation',
    TRANSACTION_CONFIRMATION: 'Transaction confirmation',
    DEFAULT_NOTICE: 'Notice of default',
  };
  const isDefault = doc.kind === 'DEFAULT_NOTICE';
  const call = isDefault ? state.calls.find((item) => item.id === doc.refId) : undefined;
  const line = call?.lines.find((item) => item.holderId === holder.id);
  return (
    <DocumentPaper
      tenant={tenant}
      holder={holder}
      issued={doc.date}
      title={titles[doc.kind] ?? doc.name}
      subtitle={fund?.name}
    >
      {isDefault && call && line ? (
        <Typography variant="body2">
          Capital call {call.number} of {money(line.amount)} was due on {date(call.dueDate)} and{' '}
          {money(line.amount - line.paid)} remains unpaid. Under the trust deed, default interest
          accrues from the due date. If the amount is not paid within 14 days of this notice, the
          trustee may exercise the remedies in the deed. Pay now in the app under Payments, quoting
          reference {line.crn}.
        </Typography>
      ) : (
        <DocTable
          head={['Date and type', 'Units', 'Amount']}
          rows={entries.map((entry) => [
            `${date(entry.effectiveDate)} · ${LEDGER_LABELS[entry.type]}`,
            entry.units ? formatUnits(entry.units) : '—',
            money(entry.amount, true),
          ])}
        />
      )}
    </DocumentPaper>
  );
}
